/**
 * seo.js — BORDS SEO Utility
 * Handles dynamic title, meta description, canonical URL, and JSON-LD schema
 * injection for every page in the React SPA.
 *
 * Usage:
 *   import { setPageMeta, injectProductSchema, injectCollectionSchema,
 *            injectBreadcrumb, removeProductSchema } from '../lib/seo';
 *
 *   // In a product page useEffect:
 *   useEffect(() => {
 *     setPageMeta({
 *       title: `BORDS ${product.name} | Heavyweight Oversized T-Shirt`,
 *       description: `Shop the BORDS ${product.name}. 240 GSM heavyweight oversized fit.`,
 *       canonical: `https://www.bords.co.in/products/${product.slug}`,
 *     });
 *     injectProductSchema(product);
 *     injectBreadcrumb([
 *       { name: 'Home', url: 'https://www.bords.co.in/' },
 *       { name: 'T-Shirts', url: 'https://www.bords.co.in/collections/oversized-t-shirts' },
 *       { name: product.name },
 *     ]);
 *     return () => removeProductSchema();
 *   }, [product]);
 */

const SITE_URL    = 'https://www.bords.co.in';
const BRAND_NAME  = 'BORDS';
const DEFAULT_TITLE = 'BORDS | Oversized Streetwear & Heavyweight T-Shirts India';
const DEFAULT_DESC  = 'Shop BORDS — Indian streetwear made for people bored of routine designs. Discover heavyweight oversized T-shirts and contemporary everyday styles.';

// ── Helpers ───────────────────────────────────────────────────────────────────

function setMeta(name, content) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement('meta'); el.name = name; document.head.appendChild(el); }
  el.setAttribute('content', content);
}

function setProperty(property, content) {
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) { el = document.createElement('meta'); el.setAttribute('property', property); document.head.appendChild(el); }
  el.setAttribute('content', content);
}

/** Normalise to www.bords.co.in and strip query params/hash from canonical */
function normaliseCanonical(url) {
  try {
    const u = new URL(url);
    // Always use www
    if (u.hostname === 'bords.co.in') u.hostname = 'www.bords.co.in';
    // Strip tracking params that shouldn't be in canonicals
    ['utm_source','utm_medium','utm_campaign','utm_content','utm_term','ref','fbclid','gclid'].forEach(p => u.searchParams.delete(p));
    // Remove trailing slash on non-root paths
    const clean = u.origin + (u.pathname === '/' ? '/' : u.pathname.replace(/\/$/, '')) + (u.search || '');
    return clean;
  } catch (_) { return url; }
}

function setCanonical(url) {
  const canonical = normaliseCanonical(url);
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) { el = document.createElement('link'); el.rel = 'canonical'; document.head.appendChild(el); }
  el.href = canonical;
  // Keep OG:URL in sync
  const og = document.querySelector('meta[property="og:url"]');
  if (og) og.setAttribute('content', canonical);
}

// ── Auto-correct canonical on first load ──────────────────────────────────────
// The static index.html canonical always points to the homepage.
// This immediately corrects it to match the actual URL being visited,
// so Google sees the right canonical even before React fully hydrates.
(function initCanonical() {
  try {
    const currentUrl = window.location.href;
    // Only update if we're not on the homepage (homepage canonical is already correct)
    if (window.location.pathname !== '/') {
      setCanonical(currentUrl);
    }
  } catch (_) {}
})();


function injectSchema(id, schema) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('script');
    el.id   = id;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(schema, null, 0);
}

function removeSchema(id) {
  const el = document.getElementById(id);
  if (el) el.remove();
}

// ── Page meta ─────────────────────────────────────────────────────────────────

/**
 * Set page-level title, description, canonical, og/twitter tags.
 * Call this in useEffect on every page/route change.
 *
 * @param {Object} opts
 * @param {string} [opts.title]       Page <title>. Appends "| BORDS" if not included.
 * @param {string} [opts.description] Meta description (max 160 chars recommended).
 * @param {string} [opts.canonical]   Canonical URL. Defaults to current URL.
 * @param {string} [opts.image]       OG image URL. Defaults to og-image.png.
 * @param {string} [opts.noindex]     Set to true to noindex (e.g. cart, auth pages).
 */
export function setPageMeta({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESC,
  canonical,
  image = `${SITE_URL}/og-image.png`,
  noindex = false,
} = {}) {
  // Ensure brand suffix
  const fullTitle = title.includes(BRAND_NAME) ? title : `${title} | ${BRAND_NAME}`;
  document.title = fullTitle;

  // Primary meta
  setMeta('title', fullTitle);
  setMeta('description', description);
  setMeta('robots', noindex ? 'noindex, nofollow' : 'index, follow');

  // Canonical
  setCanonical(canonical || window.location.href.split('?')[0]);

  // Open Graph
  setProperty('og:title', fullTitle);
  setProperty('og:description', description);
  setProperty('og:url', canonical || window.location.href.split('?')[0]);
  setProperty('og:image', image);
  setProperty('og:image:secure_url', image);

  // Twitter
  setMeta('twitter:title', fullTitle);
  setMeta('twitter:description', description);
  setMeta('twitter:image', image);
}

/**
 * Reset to homepage defaults — call when navigating back to home.
 */
export function resetPageMeta() {
  setPageMeta({
    title:       DEFAULT_TITLE,
    description: DEFAULT_DESC,
    canonical:   SITE_URL + '/',
  });
}

// ── Product Schema (JSON-LD) ───────────────────────────────────────────────────

/**
 * Inject Product structured data for a product page.
 * Only adds AggregateRating if genuine review data exists.
 *
 * @param {Object} product  — product object from API
 * @param {Object} [review] — { avgRating, reviewCount } from reviews API
 */
export function injectProductSchema(product, review = null) {
  if (!product) return;

  const rawName = (product.NAME || product.name || '').trim();
  const rawId   = product.ID ?? product.id ?? '';
  const slug    = product.slug || (rawName ? `bords-${rawName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}` : `product-${rawId}`);

  // Parse images from IMAGE_URLS (JSON string or array) or IMAGE_URL
  let images = [];
  try {
    const rawUrls = product.IMAGE_URLS || product.image_urls;
    if (rawUrls) {
      const parsed = Array.isArray(rawUrls) ? rawUrls : JSON.parse(rawUrls);
      if (Array.isArray(parsed)) images = parsed.filter(Boolean);
    }
  } catch (_) {}
  if (!images.length) {
    const single = product.IMAGE_URL || product.image_url;
    if (single) images = [single];
  }
  if (!images.length) {
    images = [`${SITE_URL}/og-image.png`];
  }

  const rawPrice = product.PRICE ?? product.price ?? 0;
  const priceNum = Number(rawPrice) || 0;
  const inStock  = (product.STOCK ?? product.stock ?? 1) > 0;
  const desc     = (product.seo_description || product.SEO_DESCRIPTION || product.DESCRIPTION || product.description || '').trim()
    || `Shop the BORDS ${rawName}. 240 GSM heavyweight oversized streetwear T-shirt. Free shipping across India.`;

  const schema = {
    '@context':    'https://schema.org',
    '@type':       'Product',
    'name':        rawName,
    'description': desc,
    'image':       images,
    'sku':         product.sku || `BORDS-${rawId}`,
    'brand': {
      '@type': 'Brand',
      'name':  BRAND_NAME,
    },
    'offers': {
      '@type':           'Offer',
      'url':             `${SITE_URL}/products/${slug}`,
      'priceCurrency':   'INR',
      'price':           priceNum.toFixed(2),
      'priceValidUntil': '2027-12-31',
      'validFrom':       product.CREATED_AT ? String(product.CREATED_AT).split('T')[0] : '2026-01-01',
      'availability':    inStock
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock',
      'itemCondition':   'https://schema.org/NewCondition',
      'seller': {
        '@type': 'Organization',
        'name':  BRAND_NAME,
      },
      'shippingDetails': {
        '@type': 'OfferShippingDetails',
        'shippingRate': {
          '@type': 'MonetaryAmount',
          'value': '0.00',
          'currency': 'INR',
        },
        'shippingDestination': {
          '@type': 'DefinedRegion',
          'addressCountry': 'IN',
        },
        'deliveryTime': {
          '@type': 'ShippingDeliveryTime',
          'handlingTime': {
            '@type': 'QuantitativeValue',
            'minValue': 0,
            'maxValue': 1,
            'unitCode': 'd',
          },
          'transitTime': {
            '@type': 'QuantitativeValue',
            'minValue': 2,
            'maxValue': 5,
            'unitCode': 'd',
          },
        },
      },
      'hasMerchantReturnPolicy': {
        '@type': 'MerchantReturnPolicy',
        'applicableCountry': 'IN',
        'returnPolicyCategory': 'https://schema.org/MerchantReturnFiniteReturnWindow',
        'merchantReturnDays': 7,
        'returnMethod': 'https://schema.org/ReturnByMail',
        'returnFees': 'https://schema.org/FreeReturn',
      },
    },
  };

  // Only add AggregateRating with real data
  if (review && review.reviewCount > 0 && review.avgRating > 0) {
    schema.aggregateRating = {
      '@type':       'AggregateRating',
      'ratingValue': String(Number(review.avgRating).toFixed(1)),
      'reviewCount': String(review.reviewCount),
      'bestRating':  '5',
      'worstRating': '1',
    };
  }

  injectSchema('bords-product-schema', schema);
}


export function removeProductSchema() {
  removeSchema('bords-product-schema');
  removeSchema('bords-breadcrumb-schema');
}

// ── BreadcrumbList Schema ─────────────────────────────────────────────────────

/**
 * Inject BreadcrumbList structured data.
 *
 * @param {Array<{name: string, url?: string}>} crumbs
 *   Last item should have no url (current page).
 */
export function injectBreadcrumb(crumbs = []) {
  const schema = {
    '@context': 'https://schema.org',
    '@type':    'BreadcrumbList',
    'itemListElement': crumbs.map((c, i) => ({
      '@type':    'ListItem',
      'position': i + 1,
      'name':     c.name,
      ...(c.url ? { 'item': c.url } : {}),
    })),
  };
  injectSchema('bords-breadcrumb-schema', schema);
}

export const injectBreadcrumbsSchema = injectBreadcrumb;

// ── Collection Schema ─────────────────────────────────────────────────────────

/**
 * Inject ItemList schema for collection pages.
 *
 * @param {string} name     — Collection display name
 * @param {Array}  products — Array of product objects
 */
export function injectCollectionSchema(name, products = []) {
  const schema = {
    '@context': 'https://schema.org',
    '@type':    'ItemList',
    'name':     name,
    'itemListElement': products.slice(0, 20).map((p, i) => {
      const rawName = (p.NAME || p.name || '').trim();
      const rawId   = p.ID ?? p.id ?? '';
      const slug    = p.slug || (rawName ? `bords-${rawName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}` : `product-${rawId}`);
      return {
        '@type':    'ListItem',
        'position': i + 1,
        'url':      `${SITE_URL}/products/${slug}`,
      };
    }),
  };
  injectSchema('bords-collection-schema', schema);
}

export function removeCollectionSchema() {
  removeSchema('bords-collection-schema');
  removeSchema('bords-breadcrumb-schema');
}

// ── FAQ Schema ────────────────────────────────────────────────────────────────

/**
 * Inject FAQPage schema.
 *
 * @param {Array<{q: string, a: string}>} faqs
 */
export function injectFAQSchema(faqs = []) {
  const schema = {
    '@context': 'https://schema.org',
    '@type':    'FAQPage',
    'mainEntity': faqs.map(({ q, a }) => ({
      '@type':    'Question',
      'name':     q,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text':  a,
      },
    })),
  };
  injectSchema('bords-faq-schema', schema);
}

export function removeFAQSchema() {
  removeSchema('bords-faq-schema');
}

// ── Article Schema (JSON-LD) ──────────────────────────────────────────────────

/**
 * Inject Article / BlogPosting structured data for journal posts.
 *
 * @param {Object} opts
 * @param {string} opts.title
 * @param {string} opts.description
 * @param {string} opts.url
 * @param {string} [opts.image]
 * @param {string} [opts.datePublished]
 * @param {string} [opts.dateModified]
 * @param {string} [opts.author]
 */
export function injectArticleSchema({
  title,
  description,
  url,
  image = `${SITE_URL}/og-image.png`,
  datePublished = '2026-09-01',
  dateModified = '2026-09-09',
  author = 'BORDS Studio Bangalore',
} = {}) {
  const schema = {
    '@context': 'https://schema.org',
    '@type':    'BlogPosting',
    'mainEntityOfPage': {
      '@type': 'WebPage',
      '@id':   url || window.location.href,
    },
    'headline':      title,
    'description':   description,
    'image':         [image],
    'datePublished': datePublished,
    'dateModified':  dateModified,
    'author': {
      '@type': 'Organization',
      'name':  author,
      'url':   SITE_URL,
    },
    'publisher': {
      '@type': 'Organization',
      'name':  BRAND_NAME,
      'logo': {
        '@type': 'ImageObject',
        'url':   `${SITE_URL}/bords-logo.png`,
      },
    },
  };
  injectSchema('bords-article-schema', schema);
}

export function removeArticleSchema() {
  removeSchema('bords-article-schema');
  removeSchema('bords-breadcrumb-schema');
}

// ── GA4 Event Helpers ─────────────────────────────────────────────────────────

function gtag(...args) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag(...args);
  }
}

/** Build a GA4-compatible item object from a BORDS product */
function toGAItem(product, opts = {}) {
  return {
    item_id:       String(product.id || product.ID || ''),
    item_name:     product.name || product.NAME || '',
    item_brand:    BRAND_NAME,
    item_category: product.category || 'T-Shirts',
    item_variant:  opts.variant || '',
    price:         Number(product.price || product.PRICE || 0),
    quantity:      opts.quantity || 1,
    index:         opts.index || 0,
    item_list_name: opts.listName || '',
  };
}

export const analytics = {
  /** Fire on every route change */
  pageView(title, url) {
    gtag('event', 'page_view', {
      page_title:    title || document.title,
      page_location: url   || window.location.href,
    });
  },

  /** Product detail page loaded */
  viewItem(product) {
    gtag('event', 'view_item', {
      currency: 'INR',
      value:    Number(product.price || 0),
      items:    [toGAItem(product)],
    });
  },

  /** User added product to cart */
  addToCart(product, variant = '', quantity = 1) {
    gtag('event', 'add_to_cart', {
      currency: 'INR',
      value:    Number(product.price || 0) * quantity,
      items:    [toGAItem(product, { variant, quantity })],
    });
  },

  /** User removed product from cart */
  removeFromCart(product, quantity = 1) {
    gtag('event', 'remove_from_cart', {
      currency: 'INR',
      value:    Number(product.price || 0) * quantity,
      items:    [toGAItem(product, { quantity })],
    });
  },

  /** Checkout page opened */
  beginCheckout(items, totalValue) {
    gtag('event', 'begin_checkout', {
      currency: 'INR',
      value:    totalValue,
      items:    items.map(i => toGAItem(i.product || i, { quantity: i.qty || i.quantity || 1 })),
    });
  },

  /** Purchase completed */
  purchase(order) {
    gtag('event', 'purchase', {
      transaction_id: String(order.id || order.orderId || ''),
      currency:       'INR',
      value:          Number(order.total || order.amount || 0),
      shipping:       Number(order.shippingCost || 0),
      items: (order.items || []).map((i, idx) =>
        toGAItem(i.product || i, { quantity: i.qty || 1, index: idx })
      ),
    });
  },

  /** Search performed */
  search(term) {
    gtag('event', 'search', { search_term: String(term) });
  },

  /** WhatsApp link clicked */
  whatsappClick(location = 'unknown') {
    gtag('event', 'whatsapp_click', { location });
  },

  /** Instagram link clicked */
  instagramClick(location = 'unknown') {
    gtag('event', 'instagram_click', { location });
  },

  /** Size guide opened */
  sizeGuideOpen(productId, productName) {
    gtag('event', 'size_guide_open', { product_id: String(productId), product_name: productName });
  },
};
